import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../theme/designTokens';

export const BRAND_MOTTO_SANSKRIT = 'dheerga drishtihi athibalayudha';
export const BRAND_MOTTO_ENGLISH = '(Foresight is the strongest weapon)';
export const BRAND_TAGLINE = 'ENGINEERING RESILIENCE';

const mottoFont = '"Samarkan", "Cormorant Garamond", Georgia, serif';
const subtitleFont = '"Cormorant Garamond", Georgia, "Times New Roman", serif';

// Inline Sanskrit motto with the ® registered mark anchored to the last word
// so the mark scales with the motto and never wraps onto its own line.
const SanskritMotto = ({ sx, registeredFontSize = '0.45em' }) => (
  <Box component="span" sx={{ display: 'inline-block', whiteSpace: 'nowrap', ...sx }}>
    {BRAND_MOTTO_SANSKRIT}
    
  </Box>
);

export const NavbarBrandTagline = ({ showOnMobile = false }) => (
  <Box
    sx={{
      display: showOnMobile ? 'block' : { xs: 'none', md: 'block' },
      mt: 0.5,
      maxWidth: { md: 280, lg: 320 },
      textAlign: 'left',
    }}
  >
    <Typography
      component="p"
      sx={{
        fontFamily: mottoFont,
        fontSize: { md: '1rem', lg: '1.1rem' },
        fontWeight: 500,
        color: colors.navyDark,
        textTransform: 'lowercase',
        letterSpacing: '0.06em',
        lineHeight: 1.1,
        m: 0,
      }}
    >
      <SanskritMotto />
    </Typography>
    <Typography
      component="p"
      sx={{
        fontFamily: subtitleFont,
        fontSize: { md: '0.78rem', lg: '0.85rem' },
        fontStyle: 'italic',
        fontWeight: 500,
        color: colors.navyDark,
        lineHeight: 1.3,
        m: 0,
        mt: 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      {BRAND_MOTTO_ENGLISH}
    </Typography>
  </Box>
);

const BrandTagline = ({ variant = 'dark', align = 'left', compact = false, banner = false }) => {
  const isLight = variant === 'light';
  const alignItems = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';

  const mottoColor = isLight ? colors.navyDark : colors.white;
  const subtitleColor = isLight ? colors.navyDark : 'rgba(255,255,255,0.85)';

  const mottoFontSize = banner
    ? compact
      ? { xs: '1.5rem', sm: '1.9rem', md: '2.15rem' }
      : { xs: '1.85rem', sm: '2.35rem', md: '2.75rem' }
    : compact
    ? { xs: '1.35rem', md: '1.5rem' }
    : { xs: '1.55rem', md: '1.75rem' };

  const subtitleFontSize = banner
    ? compact
      ? { xs: '0.85rem', md: '0.95rem' }
      : { xs: '0.95rem', md: '1.1rem' }
    : compact
    ? { xs: '0.8rem', md: '0.9rem' }
    : { xs: '0.9rem', md: '1rem' };

  const mottoTextShadow =
    !isLight && banner
      ? '0 1px 0 rgba(255,255,255,0.35), 0 2px 8px rgba(0,0,0,0.32)'
      : 'none';

  return (
    <Box
      sx={{
        textAlign: align,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems,
      }}
    >
      <Typography
        component="p"
        sx={{
          fontFamily: mottoFont,
          fontSize: mottoFontSize,
          fontWeight: isLight ? 500 : 400,
          color: mottoColor,
          textTransform: 'lowercase',
          letterSpacing: '0.07em',
          lineHeight: 1.15,
          m: 0,
          textShadow: mottoTextShadow,
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility',
        }}
      >
        <SanskritMotto />
      </Typography>
      <Typography
        sx={{
          fontFamily: subtitleFont,
          fontSize: subtitleFontSize,
          fontStyle: 'italic',
          fontWeight: isLight ? 500 : 400,
          color: subtitleColor,
          mt: banner ? 1 : 0.5,
          lineHeight: 1.4,
          textAlign: align,
        }}
      >
        {BRAND_MOTTO_ENGLISH}
      </Typography>
      {!compact && (
        <Typography
          sx={{
            fontSize: banner ? { xs: '0.65rem', md: '0.7rem' } : { xs: '0.7rem', md: '0.75rem' },
            fontWeight: 700,
            letterSpacing: 2,
            color: isLight ? colors.navyMid : colors.ochre,
            mt: banner ? 1.25 : 1.25,
            textTransform: 'uppercase',
            textAlign: align,
          }}
        >
          {BRAND_TAGLINE}
        </Typography>
      )}
    </Box>
  );
};

export default BrandTagline;
